import React, { useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import CloseIcon from "@material-ui/icons/Close";
import GetAppIcon from "@material-ui/icons/GetApp";

import { formatCurrency, formatDate, formatDateTime } from "./format";
import { downloadCsv, csvNumber } from "./csv";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  title: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    paddingBottom: theme.spacing(1),
  },
  totalBanner: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    borderLeftWidth: 4,
    borderLeftStyle: "solid",
  },
  section: {
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  tableWrap: {
    width: "100%",
    overflowX: "auto",
    ...theme.scrollbarStyles,
  },
  nowrap: {
    whiteSpace: "nowrap",
  },
}));

// Rótulos traduzidos na renderização/exportação para acompanhar a troca de idioma
const PAY_STATUS = {
  approved: { labelKey: "billingManager.paymentStatus.approved", color: "#4caf50" },
  pending: { labelKey: "billingManager.paymentStatus.pending", color: "#ff9800" },
  rejected: { labelKey: "billingManager.paymentStatus.rejected", color: "#f44336" },
  cancelled: { labelKey: "billingManager.paymentStatus.cancelled", color: "#9e9e9e" },
  refunded: { labelKey: "billingManager.paymentStatus.refunded", color: "#607d8b" },
};

const payStatusLabel = (status) =>
  PAY_STATUS[status] ? i18n.t(PAY_STATUS[status].labelKey) : status;

const methodLabel = (method) => {
  if (method === "pix") return "PIX";
  if (method === "credit_card") return i18n.t("billingManager.methods.creditCard");
  return method;
};

const typeLabel = (type) => {
  if (type === "one_time") return i18n.t("billingManager.types.oneTime");
  if (type === "recurring") return i18n.t("billingManager.types.recurring");
  return type;
};

export default function CardDetailDialog({
  open,
  onClose,
  title,
  subtitle,
  color,
  kind, // "payments" | "receivables"
  rows,
}) {
  const classes = useStyles();
  const safeRows = useMemo(() => rows || [], [rows]);

  const byCompany = useMemo(() => {
    const map = new Map();
    safeRows.forEach((r) => {
      const cur = map.get(r.companyId) || {
        companyId: r.companyId,
        name: r.companyName,
        count: 0,
        total: 0,
      };
      cur.count += 1;
      cur.total += Number(r.value || 0);
      map.set(r.companyId, cur);
    });
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [safeRows]);

  const total = useMemo(
    () => safeRows.reduce((acc, r) => acc + Number(r.value || 0), 0),
    [safeRows]
  );

  const handleExport = () => {
    const slug = String(title || i18n.t("billingManager.detail.fileFallback"))
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "");
    if (kind === "payments") {
      downloadCsv(`${slug}.csv`, [
        [
          i18n.t("common.date"),
          i18n.t("common.company"),
          i18n.t("common.description"),
          i18n.t("billingManager.columns.method"),
          i18n.t("common.type"),
          i18n.t("billingManager.columns.status"),
          i18n.t("billingManager.columns.mpPaymentId"),
          i18n.t("billingManager.columns.value"),
        ],
        ...safeRows.map((r) => [
          formatDateTime(r.date),
          r.companyName,
          r.description,
          methodLabel(r.method),
          typeLabel(r.type),
          payStatusLabel(r.status),
          r.mpPaymentId || "",
          csvNumber(r.value),
        ]),
      ]);
      return;
    }
    downloadCsv(`${slug}.csv`, [
      [
        i18n.t("billingManager.columns.dueDate"),
        i18n.t("common.company"),
        i18n.t("common.description"),
        i18n.t("billingManager.columns.origin"),
        i18n.t("billingManager.columns.daysOverdue"),
        i18n.t("billingManager.columns.value"),
      ],
      ...safeRows.map((r) => [
        formatDate(r.dueDate),
        r.companyName,
        r.detail,
        r.source === "invoice"
          ? i18n.t("billingManager.origin.invoice")
          : i18n.t("billingManager.origin.companyDueDate"),
        r.daysOverdue || 0,
        csvNumber(r.value),
      ]),
    ]);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle disableTypography className={classes.title}>
        <div>
          <Typography variant="h6">{title}</Typography>
          {subtitle && (
            <Typography variant="body2" color="textSecondary">
              {subtitle}
            </Typography>
          )}
        </div>
        <IconButton size="small" onClick={onClose} aria-label={i18n.t("common.close")}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Paper
          variant="outlined"
          className={classes.totalBanner}
          style={{ borderLeftColor: color }}
        >
          <Typography variant="h6" style={{ color, fontWeight: "bold" }}>
            {formatCurrency(total)}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {i18n.t("billingManager.detail.entries", { count: safeRows.length })} ·{" "}
            {i18n.t("billingManager.detail.clients", { count: byCompany.length })}
          </Typography>
        </Paper>

        {safeRows.length === 0 ? (
          <Typography variant="body2" color="textSecondary" align="center">
            {i18n.t("billingManager.detail.empty")}
          </Typography>
        ) : (
          <>
            <Typography variant="subtitle2" className={classes.section}>
              {i18n.t("billingManager.detail.byClient")}
            </Typography>
            <Paper variant="outlined" className={classes.tableWrap}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{i18n.t("common.company")}</TableCell>
                    <TableCell align="center">
                      {i18n.t("billingManager.columns.entries")}
                    </TableCell>
                    <TableCell align="right">{i18n.t("common.total")}</TableCell>
                    <TableCell align="right">
                      {i18n.t("billingManager.columns.cardShare")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {byCompany.map((c) => (
                    <TableRow key={c.companyId}>
                      <TableCell>{c.name}</TableCell>
                      <TableCell align="center">{c.count}</TableCell>
                      <TableCell align="right" className={classes.nowrap}>
                        <strong>{formatCurrency(c.total)}</strong>
                      </TableCell>
                      <TableCell align="right" className={classes.nowrap}>
                        {total > 0
                          ? `${((c.total / total) * 100).toFixed(1)}%`
                          : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>

            <Typography variant="subtitle2" className={classes.section}>
              {i18n.t("billingManager.detail.details")}
            </Typography>
            <Paper variant="outlined" className={classes.tableWrap}>
              {kind === "payments" ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{i18n.t("common.date")}</TableCell>
                      <TableCell>{i18n.t("common.company")}</TableCell>
                      <TableCell>{i18n.t("common.description")}</TableCell>
                      <TableCell align="center">
                        {i18n.t("billingManager.columns.method")}
                      </TableCell>
                      <TableCell align="center">{i18n.t("common.type")}</TableCell>
                      <TableCell align="center">
                        {i18n.t("billingManager.columns.status")}
                      </TableCell>
                      <TableCell align="right">
                        {i18n.t("billingManager.columns.value")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {safeRows.map((r) => {
                      const info = PAY_STATUS[r.status];
                      return (
                        <TableRow key={r.id}>
                          <TableCell className={classes.nowrap}>
                            {formatDateTime(r.date)}
                          </TableCell>
                          <TableCell>{r.companyName}</TableCell>
                          <TableCell>
                            {r.description}
                            {r.statusDetail && r.status !== "approved" && (
                              <Typography
                                variant="caption"
                                display="block"
                                color="textSecondary"
                              >
                                {r.statusDetail}
                              </Typography>
                            )}
                            {r.mpPaymentId && (
                              <Typography
                                variant="caption"
                                display="block"
                                color="textSecondary"
                              >
                                MP {r.mpPaymentId}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {methodLabel(r.method) || "-"}
                          </TableCell>
                          <TableCell align="center">
                            {typeLabel(r.type)}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={payStatusLabel(r.status)}
                              style={{
                                backgroundColor: info?.color || "#9e9e9e",
                                color: "#fff",
                              }}
                            />
                          </TableCell>
                          <TableCell align="right" className={classes.nowrap}>
                            <strong>{formatCurrency(r.value)}</strong>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{i18n.t("billingManager.columns.dueDate")}</TableCell>
                      <TableCell>{i18n.t("common.company")}</TableCell>
                      <TableCell>{i18n.t("common.description")}</TableCell>
                      <TableCell align="center">
                        {i18n.t("billingManager.columns.origin")}
                      </TableCell>
                      <TableCell align="center">
                        {i18n.t("billingManager.columns.status")}
                      </TableCell>
                      <TableCell align="right">
                        {i18n.t("billingManager.columns.value")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {safeRows.map((r) => (
                      <TableRow key={`${r.source}-${r.invoiceId || r.companyId}`}>
                        <TableCell
                          className={classes.nowrap}
                          style={
                            r.daysOverdue > 0
                              ? { color: "#f44336", fontWeight: "bold" }
                              : undefined
                          }
                        >
                          {formatDate(r.dueDate)}
                        </TableCell>
                        <TableCell>
                          {r.companyName}
                          {r.companyActive === false && (
                            <Typography
                              variant="caption"
                              display="block"
                              color="textSecondary"
                            >
                              {i18n.t("billingManager.companyDisabled")}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{r.detail}</TableCell>
                        <TableCell align="center">
                          <Typography variant="caption">
                            {r.source === "invoice"
                              ? i18n.t("billingManager.origin.invoiceNumber", {
                                  id: r.invoiceId,
                                })
                              : i18n.t("billingManager.origin.companyDueDate")}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {r.daysOverdue > 0 ? (
                            <Chip
                              size="small"
                              label={i18n.t("billingManager.days", {
                                count: r.daysOverdue,
                              })}
                              style={{ backgroundColor: "#f44336", color: "#fff" }}
                            />
                          ) : (
                            <Chip
                              size="small"
                              label={i18n.t("billingManager.notDueYet")}
                              style={{ backgroundColor: "#1976d2", color: "#fff" }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right" className={classes.nowrap}>
                          <strong>{formatCurrency(r.value)}</strong>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Box flexGrow={1} paddingLeft={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<GetAppIcon />}
            onClick={handleExport}
            disabled={safeRows.length === 0}
          >
            CSV
          </Button>
        </Box>
        <Button onClick={onClose} color="primary">
          {i18n.t("common.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
